/**
 * list-kartoteka — mapowanie listy w Kreatorze na klucz kartoteki dla akcji.
 *
 * Zakładka „Akcje" musi wiedzieć, KTÓRĄ kartoteką jest edytowana lista, żeby
 * pokazać właściwy podzbiór katalogu (inne akcje dla kontrahentów, inne dla
 * faktur — KSeF/FK tylko tam). Kartoteki systemowe „core" mają slug listy
 * równy kluczowi kartoteki (`contractors`, `employees`…); listy dokumentów mają
 * slug mnogi mapowany na klasę (`sale_invoices` → `sale_invoice`); wszystko inne
 * (moduł własny tenanta, moduł systemowy złożony w Kreatorze u ITE) = lista
 * generyczna, na której dostępne są tylko akcje uniwersalne.
 */

import {
  GENERIC_LIST_KARTOTEKA_KEY,
  isRecordCategoryKartotekaKey,
  type ListActionKartotekaKey,
} from '@saas/shared';

export interface ListKartotekaResolution {
  kartotekaKey: ListActionKartotekaKey;
  /** Klasa dokumentu — tylko gdy `kartotekaKey === 'documents'`. */
  documentClass?: string;
}

/**
 * Slugi list dokumentów (slug systemu = `ListBuilderMenu slug`) → klasa dokumentu.
 * Komplet 7 klas: faktury sprzedaży/zakupu + dokumenty handlowe, umowy, pisma,
 * dokumenty wewnętrzne, dokumenty specjalne.
 */
const DOCUMENT_LIST_SLUGS: Readonly<Record<string, string>> = {
  sale_invoices: 'sale_invoice',
  purchase_invoices: 'purchase_invoice',
  commercial: 'commercial',
  contracts: 'contract',
  letters: 'letter',
  internal: 'internal',
  specjalne: 'specjalne',
};

export function resolveListKartoteka(
  scope: 'system' | 'module' | 'template',
  slug: string | null | undefined,
): ListKartotekaResolution {
  // Moduł własny tenanta / szablon systemowy z Kreatora u ITE = lista generyczna.
  if (scope !== 'system' || !slug) {
    return { kartotekaKey: GENERIC_LIST_KARTOTEKA_KEY };
  }
  const documentClass = DOCUMENT_LIST_SLUGS[slug];
  if (documentClass) return { kartotekaKey: 'documents', documentClass };
  if (isRecordCategoryKartotekaKey(slug)) return { kartotekaKey: slug };
  // Kartoteka systemowa z Kreatora u ITE, nie „core" → generyczna.
  return { kartotekaKey: GENERIC_LIST_KARTOTEKA_KEY };
}
